import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { lives } from "@/mocks/lives";
import { channels } from "@/mocks/channels";
import { formatViewers } from "@/utils/format";

const slideIds = ["g1", "e1", "sp1", "mu1"];
const slides = slideIds
  .map((id) => lives.find((live) => live.id === id))
  .filter((live): live is (typeof lives)[number] => Boolean(live));

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goPrev = () => setActive((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setActive((prev) => (prev + 1) % slides.length);

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden rounded-none md:rounded-lg">
      {slides.map((slide, index) => {
        const channel = channels.find((item) => item.key === slide.channel);
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <img
              src={slide.cover}
              alt={`${slide.title} - ${slide.streamer}`}
              title={`${slide.title} - ${slide.streamer}`}
              className="w-full h-full object-top object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/15" />
            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-6 md:px-16">
                <div className="max-w-2xl">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-label font-semibold text-background-50">
                      <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-live-pulse" />
                      {slide.status === "online" ? "正在直播" : "精彩回放"}
                    </span>
                    {channel && (
                      <Link
                        to={`/category/${channel.key}`}
                        className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-label text-background-50 hover:bg-background-50/25 transition-colors"
                      >
                        {channel.name}
                      </Link>
                    )}
                  </div>
                  <h2 className="font-heading text-2xl md:text-5xl font-extrabold text-background-50 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="mt-4 text-sm md:text-base text-background-100/90 max-w-xl leading-relaxed line-clamp-2">
                    {slide.desc}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/live/${slide.id}`)}
                      className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg" />
                      立即观看
                    </button>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full overflow-hidden border-2 border-background-50/60">
                        <img
                          src={slide.avatar}
                          alt={slide.streamer}
                          title={`${slide.streamer} - 主播`}
                          className="w-full h-full object-top object-cover"
                        />
                      </span>
                      <div>
                        <p className="text-sm font-label text-background-50">{slide.streamer}</p>
                        <p className="text-xs text-background-100/80 flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {formatViewers(slide.viewers)} 人在看
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={goPrev}
        aria-label="上一个"
        className="absolute z-20 left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/20 backdrop-blur flex items-center justify-center text-background-50 hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="下一个"
        className="absolute z-20 right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/20 backdrop-blur flex items-center justify-center text-background-50 hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute z-20 bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === active ? "w-8 bg-background-50" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}